/* eslint-disable no-nested-ternary */
import { ColumnVisibilityButton } from './components/ColumnVisibilityButton'
import { TableToolbar } from './components/TableToolbar'
import { useTableControls, TableColumn } from './hooks/useTableControls'
import { getAllOrganizations } from './services/organizationService'
import { organizationsTheme, emotionCache } from './theme/organizationsTheme'
import { CacheProvider } from '@emotion/react'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  LocationOn as LocationOnIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import React, { useState, useMemo, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { withRouter } from 'react-router-dom'

interface Props {
  readonly history: { push: (path: string) => void }
}

interface OrgRow {
  id: number
  name: string
  contact: string
  users: number
  cities: number
  status: string
  subscriptionStart: string
  subscriptionExpiry: string
}

const OrganizationsList: React.FC<Props> = ({ history }) => {
  const intl = useIntl()
  const [searchQuery, setSearchQuery] = useState('')
  const [organizations, setOrganizations] = useState<OrgRow[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)

  useEffect(() => {
    setLoading(true)
    setOrganizations([])
    getAllOrganizations(showArchived)
      .then((data) => {
        const mapped: OrgRow[] = data.map((org) => ({
          id: org.id,
          name: org.name || '',
          contact: org.contact || '',
          users: org.user_count || 0,
          cities: org.city_count || 0,
          status: org.status || 'Active',
          subscriptionStart: org.subscription_start || '',
          subscriptionExpiry: org.subscription_expiry || '',
        }))
        setOrganizations(mapped)
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load organizations:', err)
        setLoadError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => setLoading(false))
  }, [showArchived])

  const filteredOrgs = organizations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.contact.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const defaultColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'name',
        label: intl.formatMessage({ id: 'organizations.table.name' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'contact',
        label: intl.formatMessage({ id: 'organizations.table.contact' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'users',
        label: intl.formatMessage({ id: 'organizations.table.users' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'cities',
        label: intl.formatMessage({ id: 'organizations.table.cities' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'subscriptionStart',
        label: intl.formatMessage({
          id: 'organizations.table.subscriptionStart',
        }),
        visible: true,
        sortable: true,
      },
      {
        id: 'subscriptionExpiry',
        label: intl.formatMessage({
          id: 'organizations.table.subscriptionExpiry',
        }),
        visible: true,
        sortable: true,
      },
      {
        id: 'status',
        label: intl.formatMessage({ id: 'organizations.table.status' }),
        visible: true,
        sortable: true,
      },
    ],
    [intl]
  )

  const {
    page,
    rowsPerPage,
    columns,
    visibleColumns,
    paginatedData,
    sortBy,
    sortDirection,
    handleChangePage,
    handleChangeRowsPerPage,
    toggleColumn,
    handleSort,
  } = useTableControls(filteredOrgs, defaultColumns)

  const getStatusColor = (
    status: string
  ): 'default' | 'error' | 'success' | 'warning' => {
    switch (status) {
      case 'Active':
        return 'success'
      case 'Expiring Soon':
        return 'warning'
      case 'Expired':
        return 'error'
      default:
        return 'default'
    }
  }

  const handleDownload = () => {
    const headers = visibleColumns.map((col) => col.label).join(',')
    const rows = filteredOrgs
      .map((org) =>
        visibleColumns
          .map((col) => {
            if (col.id === 'name') return `"${org.name}"`
            if (col.id === 'contact') return org.contact
            if (col.id === 'users') return org.users
            if (col.id === 'cities') return org.cities
            if (col.id === 'subscriptionStart') return org.subscriptionStart
            if (col.id === 'subscriptionExpiry') return org.subscriptionExpiry
            if (col.id === 'status') return org.status
            return ''
          })
          .join(',')
      )
      .join('\n')

    const csv = `${headers}\n${rows}`
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `organizations_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={organizationsTheme}>
        <Box
          sx={{
            maxWidth: 1800,
            mx: 'auto',
            p: { xs: 2, sm: 3, md: 4 },
            pt: { xs: 10, md: 4 },
            color: '#fff',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: 4,
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
              >
                <FormattedMessage id="organizations.page.title" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="organizations.page.subtitle" />
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                  />
                }
                label="Archived"
              />
              <Button
                onClick={() => history.push('/organizations/new')}
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <FormattedMessage id="organizations.createOrg" />
              </Button>
            </Box>
          </Box>

          {loadError !== null && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                bgcolor: 'rgba(244, 67, 54, 0.12)',
                border: '1px solid rgba(244, 67, 54, 0.5)',
              }}
            >
              <Typography variant="subtitle1" color="error">
                <FormattedMessage id="organizations.error.loadFailed" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {loadError}
              </Typography>
            </Box>
          )}

          {/* Main Card */}
          <Card>
            <Box
              sx={{
                p: 3,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <TextField
                fullWidth
                disabled={loading}
                placeholder={intl.formatMessage({
                  id: 'organizations.searchPlaceholder',
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
              >
                <FormattedMessage id="organizations.download" />
              </Button>
              <ColumnVisibilityButton
                columns={columns}
                onToggleColumn={toggleColumn}
                disabled={loading}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {visibleColumns.map((column) => (
                      <TableCell key={column.id}>
                        {column.sortable !== false ? (
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={
                              sortBy === column.id ? sortDirection : 'asc'
                            }
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumns.length}
                        sx={{ py: 6, textAlign: 'center', border: 0 }}
                      >
                        <CircularProgress size={28} />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          <FormattedMessage
                            id="organizations.loading"
                            defaultMessage="Loading organizations..."
                          />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {!loading &&
                    paginatedData.length === 0 &&
                    loadError === null && (
                      <TableRow>
                        <TableCell
                          colSpan={visibleColumns.length}
                          sx={{
                            py: 6,
                            textAlign: 'center',
                            border: 0,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                              id="organizations.empty"
                              defaultMessage="No organizations found"
                            />
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  {!loading &&
                    paginatedData.map((org) => (
                      <TableRow
                        key={org.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() =>
                          history.push(`/organizations/${org.id}/edit`)
                        }
                      >
                        {visibleColumns.map((column) => {
                          if (column.id === 'name') {
                            return (
                              <TableCell key={column.id}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: 1,
                                      bgcolor: 'rgba(170, 209, 48, 0.1)',
                                      borderRadius: 2,
                                    }}
                                  >
                                    <BusinessIcon
                                      sx={{ color: 'primary.main' }}
                                    />
                                  </Box>
                                  <Typography variant="body1">
                                    {org.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )
                          }
                          if (column.id === 'contact')
                            return (
                              <TableCell key={column.id}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {org.contact}
                                </Typography>
                              </TableCell>
                            )
                          if (column.id === 'users')
                            return (
                              <TableCell key={column.id}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <PeopleIcon fontSize="small" color="action" />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {org.users}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )
                          if (column.id === 'cities')
                            return (
                              <TableCell key={column.id}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <LocationOnIcon
                                    fontSize="small"
                                    color="action"
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {org.cities}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )
                          if (column.id === 'subscriptionStart')
                            return (
                              <TableCell key={column.id}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {org.subscriptionStart
                                    ? new Date(
                                        org.subscriptionStart
                                      ).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })
                                    : 'N/A'}
                                </Typography>
                              </TableCell>
                            )
                          if (column.id === 'subscriptionExpiry')
                            return (
                              <TableCell key={column.id}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {org.subscriptionExpiry
                                    ? new Date(
                                        org.subscriptionExpiry
                                      ).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })
                                    : 'N/A'}
                                </Typography>
                              </TableCell>
                            )
                          if (column.id === 'status')
                            return (
                              <TableCell key={column.id}>
                                <Chip
                                  label={
                                    org.status === 'Active'
                                      ? intl.formatMessage({
                                          id: 'organizations.status.active',
                                        })
                                      : org.status === 'Expiring Soon'
                                      ? intl.formatMessage({
                                          id: 'organizations.status.expiringSoon',
                                        })
                                      : org.status === 'Expired'
                                      ? intl.formatMessage({
                                          id: 'organizations.status.expired',
                                        })
                                      : org.status
                                  }
                                  color={getStatusColor(org.status)}
                                  size="small"
                                />
                              </TableCell>
                            )
                          return null
                        })}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableToolbar
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={filteredOrgs.length}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Card>
        </Box>
      </ThemeProvider>
    </CacheProvider>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default withRouter(OrganizationsList)
